<script setup lang="ts">
import KgrAccordion from '@legacy/components/common/accordion/Accordion.vue';
import KgrAccordionItem from '@legacy/components/common/accordion/AccordionItem.vue';
import KgrCheckbox from '@legacy/components/common/form/checkbox/Checkbox.vue';
import KgrNoticeBox from '@legacy/components/common/NoticeBox.vue';
import KgrTable from '@legacy/components/common/table/Table.vue';
import axios from 'axios';
import { computed, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphField } from '@/components/base/field';
import { SphSwitch } from '@/components/base/switch';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps({
    federationId: {
        type: Number,
        required: true,
    },
});

const { notify } = useNotifications();

interface Organization {
    id: number
    name: string
    customerId: string
    enabled: boolean
}

const federation2FA = ref(false);
const kindergartens = ref<Organization[]>([]);
const clubs = ref<Organization[]>([]);
const pending = ref<boolean>(false);
const loading = ref<boolean>(false);

const allKindergartensEnabled = computed(() =>
    kindergartens.value.length > 0 && kindergartens.value.every((k) => k.enabled),
);

const allClubsEnabled = computed(() =>
    clubs.value.length > 0 && clubs.value.every((c) => c.enabled),
);

function toggleAllKindergartens() {
    const newValue = !allKindergartensEnabled.value;
    kindergartens.value.forEach((k) => k.enabled = newValue);
}

function toggleAllClubs() {
    const newValue = !allClubsEnabled.value;
    clubs.value.forEach((c) => c.enabled = newValue);
}

function update() {
    pending.value = true;
    axios.put(`/federations/${props.federationId}/settings/2fa`, {
        federation: federation2FA.value,
        kindergartens: {
            enabled: kindergartens.value.filter((k) => k.enabled).map((k) => k.id),
            disabled: kindergartens.value.filter((k) => !k.enabled).map((k) => k.id),
        },
        clubs: {
            enabled: clubs.value.filter((k) => k.enabled).map((k) => k.id),
            disabled: clubs.value.filter((k) => !k.enabled).map((k) => k.id),
        },
    })
        .then(() => {
            notify.success('saveChanges.success');
            load();
        })
        .finally(() => {
            pending.value = false;
        });
}
function load() {
    loading.value = true;
    axios.get(`/federations/${props.federationId}/settings/2fa`)
        .then((response) => {
            kindergartens.value = response.data.data.kindergartens;
            clubs.value = response.data.data.clubs;
            federation2FA.value = response.data.data.federation;
        })
        .catch((reason) => {
            console.error(reason);
        })
        .finally(() => {
            loading.value = false;
        });
}
load();
</script>

<template>
    <div>
        <h3 class="kgr-headline kgr-headline--h3">
            {{ $t('Einstellung Zwei-Faktor-Authentifizierung') }}
        </h3>
        <br />
        <KgrNoticeBox variant="warning">
            <template #headline>
                {{ $t('Hinweis') }}
            </template>
            <template #body>
                {{ $t('Wird die Zwei-Faktor Authentifizierung verpflichtend aktiviert, müssen Nutzer die aktuellste Version der App installieren, alte App-Versionen werden nicht unterstützt.') }}
            </template>
        </KgrNoticeBox>
        <KgrAccordion>
            <KgrAccordionItem :title="$t('Träger')">
                <p>{{ $t('Mit dieser Option kann die Zwei-Faktor-Authentifizierung als Pflicht für alle Träger-Mitarbeiter aktiviert werden.') }}</p>
                <SphField
                    :label="$t('2FA als Pflicht für alle Mitarbeiter aktivieren / deaktivieren')"
                    label-for="federation-2fa-switch"
                    inline
                    class="my-6"
                >
                    <SphSwitch
                        id="federation-2fa-switch"
                        v-model="federation2FA"
                        inline
                    />
                </SphField>
            </KgrAccordionItem>
            <KgrAccordionItem :title="$t('Einrichtungen')">
                <p>{{ $t('Mit dieser Option kann die Zwei-Faktor-Authentifizierung als Pflicht für alle Einrichtungs-Mitarbeiter aktiviert werden.') }}</p>
                <br />
                <KgrTable
                    v-if="kindergartens.length > 0"
                    class="w-full"
                    :searching="false"
                    :is-data-table="false"
                >
                    <thead>
                        <tr class="kgr-table__row">
                            <kgr-th class="kgr-table__cell--noGrow">
                                <KgrCheckbox
                                    :checked="allKindergartensEnabled"
                                    @change="toggleAllKindergartens"
                                />
                            </kgr-th>
                            <kgr-th>{{ $t('Einrichtung') }}</kgr-th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="kindergarten in kindergartens"
                            :key="kindergarten.id"
                            class="kgr-table__row"
                        >
                            <td class="kgr-table__cell">
                                <KgrCheckbox v-model="kindergarten.enabled" />
                            </td>
                            <td class="kgr-table__cell">
                                {{ kindergarten.name }}
                            </td>
                        </tr>
                    </tbody>
                </KgrTable>
            </KgrAccordionItem>
            <KgrAccordionItem :title="$t('Vereine')">
                <p>{{ $t('Mit dieser Option kann die Zwei-Faktor-Authentifizierung als Pflicht für alle Vereins-Mitarbeiter aktiviert werden.') }}</p>
                <br />
                <KgrTable
                    v-if="clubs.length > 0"
                    class="w-full"
                    :searching="false"
                    :is-data-table="false"
                >
                    <thead>
                        <tr class="kgr-table__row">
                            <kgr-th class="kgr-table__cell--noGrow">
                                <KgrCheckbox
                                    :checked="allClubsEnabled"
                                    @change="toggleAllClubs"
                                />
                            </kgr-th>
                            <kgr-th>{{ $t('Verein') }}</kgr-th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="club in clubs"
                            :key="club.id"
                            class="kgr-table__row"
                        >
                            <td class="kgr-table__cell">
                                <KgrCheckbox v-model="club.enabled" />
                            </td>
                            <td class="kgr-table__cell">
                                {{ club.name }}
                            </td>
                        </tr>
                    </tbody>
                </KgrTable>
            </KgrAccordionItem>
        </KgrAccordion>
        <hr class="kgr-separator kgr-separator--light" />
        <div class="flex justify-end">
            <SphButton
                :label="$t('Speichern')"
                :loading="pending"
                variant="primary"
                class="align-right"
                @click="update"
            />
        </div>
    </div>
</template>

<style scoped lang="scss" rel="stylesheet/scss"></style>
